import type { I18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { type ReactChild, type VFC } from 'react'

import IconCaretDown from 'views/components/atoms/Icon/IconCaretDown'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import { CONTACT_LENS_GROUP_ID } from 'views/containers/browser/ProductVariantsAttributesContainer/consts'

import type { Product, ProductAttributes, VariantAttribute } from '..'

import AttributePickerValue from './AttributePickerValue'

export const CLIENTSIDE_HOOK_CHILDREN_WRAPPER =
  'productVariantsAttribute-Wrapper'

interface AttributePickerRegionCollapsibleWrapperProps {
  children: ReactChild
  i18n: I18n
  variantAttribute: ProductAttributes
  isSingleAttributeProduct: boolean
  packageSizeAttribute: boolean
  selectedVariant?: Product
  isMasterPage: boolean
  displayLabel: boolean
  hasUnitPricing: boolean
}

const AttributePickerRegionCollapsibleWrapper: VFC<
  AttributePickerRegionCollapsibleWrapperProps
> = ({
  children,
  i18n,
  variantAttribute,
  isSingleAttributeProduct,
  packageSizeAttribute,
  selectedVariant,
  isMasterPage,
  displayLabel,
  hasUnitPricing,
}) => {
  const COLLAPSIBLE_ITEMS_THRESHOLD = 6
  const isCollapsible: boolean =
    variantAttribute.values.length > COLLAPSIBLE_ITEMS_THRESHOLD
  const hasTileTeaser =
    isSingleAttributeProduct && !packageSizeAttribute && isCollapsible
  const hasPillTeaser = !isSingleAttributeProduct && isCollapsible

  const selectedVariantAttbute = selectedVariant?.variantAttributes?.find(
    (varAttribute: VariantAttribute) => {
      return varAttribute.variantCode === variantAttribute.variantCode
    }
  )
  const isContactLens = variantAttribute.groupId === CONTACT_LENS_GROUP_ID

  const childClasses = classNames({
    'o-ProductVariantsAttributePicker-tile-teaser':
      hasTileTeaser && !hasUnitPricing,
    'o-ProductVariantsAttributePicker-pill-teaser': hasPillTeaser,
    'o-ProductVariantsAttributePicker-tile-teaser-unitPrice':
      hasTileTeaser && hasUnitPricing,
  })

  const regionLabelClasses = 'a-p l-flex u-margin-x-small--bottom'

  const collapsibleLabel = (
    <div
      role="button"
      className="o-ProductVariantsAttributePicker-Collapsible u-padding--top"
      data-clientside-hook="attributePicker-collapsible--label"
      data-trans-open={i18n?._('general.label.showNMore', {
        count: variantAttribute.values.length,
      })}
      data-trans-close={i18n?._('general.label.showLess')}
      data-current-state="closed"
    >
      <TextWithIcon icon={<IconCaretDown size="small" />} right centered>
        <Trans
          id="general.label.showNMore"
          values={{
            count: variantAttribute.values.length - COLLAPSIBLE_ITEMS_THRESHOLD,
          }}
        />
      </TextWithIcon>
    </div>
  )

  const getVariantLabel = (
    variantAttributes: ProductAttributes,
    contactLens: boolean
  ) => {
    if (contactLens) {
      return (
        <div className="u-padding--bottom">
          <Trans id="variantAttributes.contactLens.regionLabel" />:
        </div>
      )
    } else {
      switch (variantAttributes.variantCode) {
        case 'color_number':
          return (
            <>
              <Trans id="variantAttributes.label.color_number" />:
            </>
          )

        case 'package_size':
          return (
            <>
              <Trans id="variantAttributes.label.package_size" />:
            </>
          )

        default:
          return <>{variantAttributes.label}:</>
      }
    }
  }

  return (
    <>
      {displayLabel && (
        <div
          className={regionLabelClasses}
          data-qa-id={`variantAttribute-${variantAttribute.code}`}
        >
          {getVariantLabel(variantAttribute, isContactLens)}
          <span className="u-margin-xx-small--left u-font-weight--bold">
            {(isMasterPage && variantAttribute.personal) ||
            variantAttribute.type === 'LIST' ? null : (
              <AttributePickerValue
                id={selectedVariant?.upid}
                packageSizeAttribute={packageSizeAttribute}
                pickerValue={selectedVariantAttbute?.value}
                variantAttributeType={variantAttribute.type}
              />
            )}
          </span>
        </div>
      )}

      <div
        data-clientside-hook={CLIENTSIDE_HOOK_CHILDREN_WRAPPER}
        className={childClasses}
      >
        {children}
      </div>
      {(hasTileTeaser || hasPillTeaser) && (
        <div className="o-ProductVariantsAttributePicker-Collapsible-mobile-hidden">
          {collapsibleLabel}
        </div>
      )}
    </>
  )
}
export default AttributePickerRegionCollapsibleWrapper
